/* ===========================================================================
   aurora.js — Aurora Audio theme behavior. Single IIFE.
   - Progressive enhancement: adds `js` class; content is usable without GSAP.
   - GSAP + ScrollTrigger + Lenis from CDN (deferred). IntersectionObserver
     fallback if GSAP fails to load. prefers-reduced-motion short-circuits motion.
   - Idempotent init via dataset flags; init(root) scoped so Theme Editor
     section reloads (shopify:section:load) re-init only their subtree.
   =========================================================================== */
(function () {
  'use strict';

  document.documentElement.classList.add('js');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  function hasGsap() { return !!(window.gsap && window.ScrollTrigger); }
  function focusableWithin(scope) {
    return scope ? scope.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') : [];
  }
  function setMainHidden(hidden) {
    ['#MainContent', '.footer'].forEach(function (selector) {
      var el = document.querySelector(selector);
      if (!el) return;
      if (hidden) el.setAttribute('aria-hidden', 'true');
      else el.removeAttribute('aria-hidden');
    });
  }

  /* ---------- smooth scroll (Lenis) ------------------------------------- */
  var lenis = null;
  function initLenis() {
    if (reduce || coarsePointer || !window.Lenis || lenis) return;
    lenis = new window.Lenis({
      duration: 0.8,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      smoothTouch: false,
      touchMultiplier: 1.5
    });
    if (hasGsap()) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      var raf = function (time) { lenis.raf(time); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  }

  /* ---------- reveals ---------------------------------------------------- */
  /* Reveal is a CSS class toggle (no scrubbed tween), so IntersectionObserver
     is used on every device — it survives mobile address-bar resize and late
     font/image layout shifts that leave GSAP ScrollTrigger start positions
     stale (the cause of reveals never firing on touch). */
  var io = null;
  function ioObserver() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    return io;
  }

  function initReveals(root) {
    var els = root.querySelectorAll('[data-reveal], .stagger');
    els.forEach(function (el) {
      if (el.dataset.revealReady) return;
      el.dataset.revealReady = '1';
      if (el.classList.contains('stagger')) {
        Array.prototype.forEach.call(el.children, function (child, i) { child.style.setProperty('--i', i); });
      }
      if (reduce || !('IntersectionObserver' in window)) { el.classList.add('is-visible'); return; }
      ioObserver().observe(el);
    });
    // failsafe: if anything is still hidden after load settles (IO edge cases on
    // some mobile browsers), reveal it so content is never stuck invisible.
    if (!reduce && 'IntersectionObserver' in window) {
      setTimeout(function () {
        root.querySelectorAll('[data-reveal]:not(.is-visible), .stagger:not(.is-visible)').forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-visible');
        });
      }, 1200);
    }
  }

  /* ---------- hero ------------------------------------------------------- */
  function initHero(root) {
    var hero = root.querySelector('[data-hero]');
    if (!hero || hero.dataset.heroReady) return;
    hero.dataset.heroReady = '1';
    if (reduce || !hasGsap()) return;
    var gsap = window.gsap;
    var items = hero.querySelectorAll('[data-hero-item]');
    gsap.from(items, { y: 28, autoAlpha: 0, duration: 0.8, ease: 'power3.out', stagger: 0.1, delay: 0.15 });
    var device = hero.querySelector('[data-hero-device]');
    if (device && !coarsePointer) {
      gsap.to(device, {
        yPercent: 18, scale: 1.1, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
      });
    }
  }

  /* ---------- product-switch scroll (pinned 3-product crossfade) -------- */
  function initProductSwitch(root) {
    var section = root.querySelector('[data-switch]');
    if (!section || section.dataset.switchReady) return;
    section.dataset.switchReady = '1';

    var layers = section.querySelectorAll('[data-switch-layer]');
    var caps = section.querySelectorAll('[data-switch-caption]');
    var dots = section.querySelectorAll('[data-switch-dot]');
    var n = layers.length;
    if (!n) return;

    var activeIndex = -1;
    function setActive(idx) {
      idx = Math.max(0, Math.min(n - 1, idx));
      if (idx === activeIndex) return;
      activeIndex = idx;
      for (var i = 0; i < n; i++) {
        if (layers[i]) layers[i].setAttribute('data-active', i === idx ? 'true' : 'false');
        if (caps[i]) caps[i].setAttribute('data-active', i === idx ? 'true' : 'false');
        if (dots[i]) dots[i].setAttribute('data-active', i === idx ? 'true' : 'false');
      }
    }

    // reduced motion / no GSAP: show the first model statically. The full lineup
    // stays discoverable in the bud-picker + product-rail sections below.
    if (reduce || !hasGsap() || coarsePointer) { setActive(0); return; }

    var gsap = window.gsap;
    setActive(0);

    var mm = gsap.matchMedia();
    // desktop: pin the stage and scrub through the three models
    mm.add('(min-width: 1024px)', function () {
      var st = window.ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: '+=' + (n * 100) + '%',
        pin: section.querySelector('[data-switch-sticky]'),
        scrub: true,
        onUpdate: function (self) { setActive(Math.floor(Math.min(0.999, self.progress) * n)); }
      });
      return function () { st.kill(); };
    });
    // mobile: native-scroll crossfade. No ScrollTrigger pin; CSS sticky keeps the
    // device in view while normal page scroll drives Pro 2 -> Pro 3 -> Gen 4.
    mm.add('(max-width: 1023px)', function () {
      var st = window.ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: 'bottom bottom',
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: function (self) { setActive(Math.floor(Math.min(0.999, self.progress) * n)); }
      });
      return function () { st.kill(); };
    });
  }

  /* ---------- bud picker ------------------------------------------------- */
  function initBudPicker(root) {
    var picker = root.querySelector('[data-picker]');
    if (!picker || picker.dataset.pickerReady) return;
    picker.dataset.pickerReady = '1';

    var tabs = picker.querySelectorAll('[data-picker-tab]');
    var arts = picker.querySelectorAll('[data-picker-art]');
    var nameEl = picker.querySelector('[data-picker-name]');
    var priceEl = picker.querySelector('[data-picker-price]');
    var cta = picker.querySelector('[data-picker-cta]');

    function select(id) {
      tabs.forEach(function (t) { t.setAttribute('aria-pressed', t.dataset.pickerTab === id ? 'true' : 'false'); });
      arts.forEach(function (a) { a.setAttribute('data-active', a.dataset.pickerArt === id ? 'true' : 'false'); });
      var active = Array.prototype.filter.call(tabs, function (t) { return t.dataset.pickerTab === id; })[0];
      if (!active) return;
      if (nameEl) nameEl.textContent = active.dataset.name || '';
      if (priceEl) priceEl.textContent = active.dataset.price || '';
      if (cta) { cta.textContent = 'Choose ' + (active.dataset.name || '') + ' →'; cta.setAttribute('href', active.dataset.url || '#'); }
      if (!reduce && hasGsap()) {
        var activeArt = Array.prototype.filter.call(arts, function (a) { return a.dataset.pickerArt === id; })[0];
        var img = activeArt ? activeArt.querySelector('img') : null;
        var textEls = [nameEl, priceEl, cta].filter(Boolean);
        window.gsap.fromTo(textEls, { y: 8, autoAlpha: 0.72 }, { y: 0, autoAlpha: 1, duration: 0.24, ease: 'power2.out', stagger: 0.025, overwrite: true });
        if (img) window.gsap.fromTo(img, { scale: 0.965, rotate: -1.2 }, { scale: 1, rotate: 0, duration: 0.36, ease: 'power3.out', overwrite: true });
      }
    }

    tabs.forEach(function (t) { t.addEventListener('click', function () { select(t.dataset.pickerTab); }); });
    var first = tabs[0]; if (first) select(first.dataset.pickerTab);
  }

  /* ---------- card hovers (mouse only) ---------------------------------- */
  function initCardHovers(root) {
    if (reduce || !hasGsap()) return;
    var cards = root.querySelectorAll('[data-card]');
    cards.forEach(function (card) {
      if (card.dataset.hoverReady) return;
      card.dataset.hoverReady = '1';
      card.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse') return;
        window.gsap.to(card, { y: -6, duration: 0.22, ease: 'power3.out' });
      });
      card.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') return;
        window.gsap.to(card, { y: 0, duration: 0.22, ease: 'power3.out' });
      });
    });
  }

  /* ---------- variant picker (PDP) -------------------------------------- */
  function initVariants(root) {
    var forms = root.querySelectorAll('[data-product-form]');
    forms.forEach(function (form) {
      if (form.dataset.variantsReady) return;
      form.dataset.variantsReady = '1';
      var idInput = form.querySelector('[name="id"]');
      var scope = form.closest('[data-buybox]') || document;
      var priceOut = scope.querySelector('[data-price]');
      var stockOut = scope.querySelector('[data-stock]');
      var stickyPrice = document.querySelector('[data-sticky-price]');
      var codButtons = document.querySelectorAll('[data-isupply-cod-open]:not([data-isupply-cod-cart])');
      var opts = form.querySelectorAll('[data-variant-option]');
      function syncCodContext(btn) {
        codButtons.forEach(function (codButton) {
          if (btn.dataset.variantId) codButton.dataset.isupplyCodVariantId = btn.dataset.variantId;
          if (btn.dataset.variantSku) codButton.dataset.isupplyCodSku = btn.dataset.variantSku;
          if (btn.dataset.priceRaw) codButton.dataset.isupplyCodPrice = btn.dataset.priceRaw;
        });
        if (window.iSupplyCodProduct) {
          if (btn.dataset.variantId) window.iSupplyCodProduct.variant_id = btn.dataset.variantId;
          if (btn.dataset.variantSku) window.iSupplyCodProduct.sku = btn.dataset.variantSku;
          if (btn.dataset.priceRaw) window.iSupplyCodProduct.price = Number(btn.dataset.priceRaw);
        }
      }
      opts.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          opts.forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
          btn.setAttribute('aria-pressed', 'true');
          if (idInput && btn.dataset.variantId) idInput.value = btn.dataset.variantId;
          if (priceOut && btn.dataset.price) priceOut.textContent = btn.dataset.price;
          if (stickyPrice && btn.dataset.price) stickyPrice.textContent = btn.dataset.price;
          if (stockOut && btn.dataset.stock) stockOut.textContent = btn.dataset.stock;
          syncCodContext(btn);
          // keep ?variant= deep link in sync
          if (btn.dataset.variantId && window.history && history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', btn.dataset.variantId);
            history.replaceState({}, '', url);
          }
        });
      });
    });
  }

  /* ---------- product form -> AJAX add ---------------------------------- */
  function initProductForm(root) {
    var forms = root.querySelectorAll('[data-product-form]');
    forms.forEach(function (form) {
      if (form.dataset.formReady) return;
      form.dataset.formReady = '1';
      form.addEventListener('submit', function (e) {
        // If the static preview stubs window.AURORA_ADD, use it; else let Shopify handle.
        if (typeof window.AURORA_ADD === 'function') {
          e.preventDefault();
          var btn = form.querySelector('[data-add]');
          var label = btn ? btn.textContent : '';
          if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }
          window.AURORA_ADD(form).then(function () {
            if (btn) { btn.textContent = 'Added ✓'; }
            openCart();
            setTimeout(function () { if (btn) { btn.textContent = label; btn.disabled = false; } }, 1800);
          });
        }
      });
    });
  }

  /* ---------- cart drawer ----------------------------------------------- */
  var lastFocus = null;
  function openCart() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('data-open', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.dataset.cartOpen = 'true';
    setMainHidden(true);
    document.body.style.overflow = 'hidden';
    var closeBtn = drawer.querySelector('[data-cart-close]');
    var panel = drawer.querySelector('.cart-drawer__panel');
    if (closeBtn) closeBtn.focus();
    else if (panel) panel.focus();
  }
  function closeCart() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    delete document.body.dataset.cartOpen;
    setMainHidden(false);
    document.body.style.overflow = '';
    if (lastFocus) lastFocus.focus();
  }

  function updateCartLine(key, quantity, control) {
    if (!key) return;
    var drawer = document.querySelector('[data-cart-drawer]');
    var status = drawer ? drawer.querySelector('[data-cart-status]') : null;
    if (status) status.textContent = 'Updating cart...';
    if (control) control.disabled = true;
    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, parseInt(quantity, 10) || 0) })
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart update failed');
      return response.json();
    }).then(function () {
      if (status) status.textContent = 'Cart updated.';
      window.location.reload();
    }).catch(function () {
      if (control) control.disabled = false;
      if (status) status.textContent = 'Cart update failed. Open full cart to edit.';
    });
  }

  function initCart(root) {
    var openers = root.querySelectorAll('[data-cart-open]');
    openers.forEach(function (b) {
      if (b.dataset.cartOpenReady) return;
      b.dataset.cartOpenReady = '1';
      b.addEventListener('click', function (e) { e.preventDefault(); openCart(); });
    });
    var drawer = root.querySelector('[data-cart-drawer]') || document.querySelector('[data-cart-drawer]');
    if (drawer && !drawer.dataset.cartReady) {
      drawer.dataset.cartReady = '1';
      var scrim = drawer.querySelector('[data-cart-scrim]');
      var closeBtn = drawer.querySelector('[data-cart-close]');
      if (scrim) scrim.addEventListener('click', closeCart);
      if (closeBtn) closeBtn.addEventListener('click', closeCart);
      drawer.addEventListener('change', function (event) {
        if (event.target && event.target.matches('[data-cart-qty]')) updateCartLine(event.target.dataset.key, event.target.value, event.target);
      });
      drawer.addEventListener('click', function (event) {
        var remove = event.target && event.target.closest('[data-cart-remove]');
        if (remove) { event.preventDefault(); updateCartLine(remove.dataset.key, 0, remove); }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') closeCart();
        // simple focus trap
        if (e.key === 'Tab' && drawer.getAttribute('data-open') === 'true') {
          var f = focusableWithin(drawer);
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
    }
  }

  /* ---------- header scroll state + mobile menu ------------------------- */
  function initHeader(root) {
    var nav = root.querySelector('[data-nav]');
    if (nav && !nav.dataset.navReady) {
      nav.dataset.navReady = '1';
      var onScroll = function () { nav.setAttribute('data-scrolled', window.scrollY > 24 ? 'true' : 'false'); };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      var toggle = nav.querySelector('[data-menu-toggle]');
      var menu = document.querySelector('[data-mobile-menu]');
      if (toggle && menu) {
        var menuLastFocus = null;
        function setMenu(open) {
          menu.setAttribute('data-open', open ? 'true' : 'false');
          menu.setAttribute('aria-hidden', open ? 'false' : 'true');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
          nav.setAttribute('data-menu-open', open ? 'true' : 'false');
          if (open) document.body.dataset.menuOpen = 'true';
          else delete document.body.dataset.menuOpen;
          document.body.style.overflow = open ? 'hidden' : '';
          setMainHidden(open);
          if (open) {
            menuLastFocus = document.activeElement;
            var targets = focusableWithin(menu);
            if (targets.length) targets[0].focus();
            else menu.focus();
          } else if (menuLastFocus && menuLastFocus.focus) {
            menuLastFocus.focus();
          }
        }
        nav.setAttribute('data-menu-open', menu.getAttribute('data-open') === 'true' ? 'true' : 'false');
        toggle.addEventListener('click', function () { setMenu(menu.getAttribute('data-open') !== 'true'); });
        menu.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') { e.preventDefault(); setMenu(false); return; }
          if (e.key !== 'Tab' || menu.getAttribute('data-open') !== 'true') return;
          var f = focusableWithin(menu);
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        });
        menu.querySelectorAll('a').forEach(function (link) {
          link.addEventListener('click', function () { setMenu(false); });
        });
      }
    }
  }

  /* ---------- sticky buy bar (PDP, mobile) ------------------------------ */
  function initStickyBar(root) {
    var bar = root.querySelector('[data-sticky-bar]');
    if (!bar || bar.dataset.stickyReady) return;
    bar.dataset.stickyReady = '1';
    var buybox = document.querySelector('[data-buybox]');
    var onScroll = function () {
      var pastTop = window.scrollY > window.innerHeight * 0.6;
      var boxVisible = false;
      if (buybox) { var r = buybox.getBoundingClientRect(); boxVisible = r.top < window.innerHeight && r.bottom > 0; }
      var blocked = document.body.dataset.cartOpen === 'true' || document.body.dataset.menuOpen === 'true' || document.body.classList.contains('isupply-cod-lock') || document.getElementById('isupply-cod-modal');
      var show = pastTop && !boxVisible && !blocked;
      bar.setAttribute('data-show', show ? 'true' : 'false');
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- spec sticky (handled by CSS sticky; reveals by initReveals) */

  /* ---------- quantity stepper ------------------------------------------ */
  function initQty(root) {
    var wraps = root.querySelectorAll('[data-qty]');
    wraps.forEach(function (wrap) {
      if (wrap.dataset.qtyReady) return;
      wrap.dataset.qtyReady = '1';
      var input = wrap.querySelector('[data-qty-input]');
      var minus = wrap.querySelector('[data-qty-minus]');
      var plus = wrap.querySelector('[data-qty-plus]');
      if (!input) return;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      function clamp() {
        var v = parseInt(input.value, 10);
        if (isNaN(v) || v < min) v = min;
        input.value = v;
        if (minus) minus.disabled = v <= min;
      }
      if (minus) minus.addEventListener('click', function () { input.value = (parseInt(input.value, 10) || min) - 1; clamp(); });
      if (plus) plus.addEventListener('click', function () { input.value = (parseInt(input.value, 10) || min) + 1; clamp(); });
      input.addEventListener('change', clamp);
      clamp();
    });
  }

  /* ---------- PDP image zoom / pan (pointer-driven) --------------------- */
  function initImageZoom(root) {
    var frames = root.querySelectorAll('[data-zoom]');
    frames.forEach(function (frame) {
      if (frame.dataset.zoomReady) return;
      frame.dataset.zoomReady = '1';
      var img = frame.querySelector('img');
      if (!img) return;
      var SCALE = 2.2;
      var zoomed = false;

      function pan(e) {
        var r = frame.getBoundingClientRect();
        var pt = (e.touches && e.touches[0]) ? e.touches[0] : e;
        var x = (pt.clientX - r.left) / r.width;
        var y = (pt.clientY - r.top) / r.height;
        x = Math.min(1, Math.max(0, x));
        y = Math.min(1, Math.max(0, y));
        var tx = (0.5 - x) * (SCALE - 1) * 100;
        var ty = (0.5 - y) * (SCALE - 1) * 100;
        img.style.transform = 'scale(' + SCALE + ') translate(' + tx + '%, ' + ty + '%)';
      }
      function zoomIn(e) { zoomed = true; frame.setAttribute('data-zoomed', 'true'); pan(e); }
      function zoomOut() { zoomed = false; frame.setAttribute('data-zoomed', 'false'); img.style.transform = ''; }

      frame.addEventListener('click', function (e) { if (zoomed) zoomOut(); else zoomIn(e); });
      frame.addEventListener('pointermove', function (e) { if (zoomed && e.pointerType === 'mouse') pan(e); });
      frame.addEventListener('touchmove', function (e) { if (zoomed) { e.preventDefault(); pan(e); } }, { passive: false });
      frame.addEventListener('pointerleave', function () { if (zoomed && !('ontouchstart' in window)) zoomOut(); });
    });
  }

  /* ---------- master init ----------------------------------------------- */
  function init(root) {
    root = root || document;
    initHeader(root);
    initReveals(root);
    initHero(root);
    initProductSwitch(root);
    initBudPicker(root);
    initCardHovers(root);
    initVariants(root);
    initQty(root);
    initImageZoom(root);
    initProductForm(root);
    initCart(root);
    initStickyBar(root);
    if (hasGsap()) window.ScrollTrigger.refresh();
  }

  function boot() {
    initLenis();
    requestAnimationFrame(function () { init(document); });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ---------- Shopify Theme Editor hooks -------------------------------- */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    if (hasGsap()) {
      window.ScrollTrigger.getAll().forEach(function (t) {
        if (t.trigger && e.target.contains(t.trigger)) t.kill();
      });
    }
  });

  // expose for preview stubs
  window.AURORA = { init: init, openCart: openCart, closeCart: closeCart };
})();
